<script setup lang="ts">
import { useToast } from '@/composables/useToast';
import { STRIPE_PUBLISHED_KEY } from '@/data/env';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { usePaymentStore } from '@/stores/payment';
import { useUserStore } from '@/stores/user';
import { Alert, Button, LayoutGap } from '@dabble/quillui';
import type { StripeElements, StripePaymentElementOptions } from '@stripe/stripe-js';
import type Stripe from 'stripe';
import { onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

interface RouteMeta {
  customer?: Stripe.Customer;
}

/** Non-empty string for Stripe `defaultValues.billingDetails`; omit empty/null. */
function billingString(value: string | null | undefined): string | undefined {
  if (value == null || typeof value !== 'string') return undefined;
  const t = value.trim();
  return t.length > 0 ? t : undefined;
}

function billingNameFromSources(meta: Stripe.Customer | undefined, userName: string | undefined): string | undefined {
  return billingString(meta?.name) ?? billingString(userName);
}

function billingEmailFromSources(meta: Stripe.Customer | undefined, userEmail: string | undefined): string | undefined {
  return billingString(meta?.email) ?? billingString(userEmail);
}

const emit = defineEmits<{
  done: [];
  cancel: [];
}>();

const { t } = useI18n();
const { showToast } = useToast();
const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const paymentStore = usePaymentStore();

const customerFromMeta = (route.meta as RouteMeta).customer;

const paymentContainer = ref<HTMLDivElement | null>(null);

let elements: StripeElements | null = null;
let alive = true;

onMounted(async () => {
  paymentStore.clearError();
  try {
    const stripe = await paymentStore.initializeStripe(STRIPE_PUBLISHED_KEY);
    if (!alive) return;

    const clientSecret = await paymentStore.createSetupIntent();
    if (!alive) return;

    const customerSessionClientSecret = paymentStore.customerSessionClientSecret || undefined;
    elements = stripe.elements({
      clientSecret,
      ...(customerSessionClientSecret ? { customerSessionClientSecret } : {}),
      loader: 'auto',
      appearance: {
        theme: 'stripe',
      },
    });
    paymentStore.setElements(elements);

    const billingName = billingNameFromSources(customerFromMeta, userStore.user.name);
    const billingEmail = billingEmailFromSources(customerFromMeta, userStore.user.email);

    const paymentOptions: StripePaymentElementOptions = {
      defaultValues: {
        billingDetails: {
          ...(billingName ? { name: billingName } : {}),
          ...(billingEmail ? { email: billingEmail } : {}),
        },
      },
      wallets: { applePay: 'auto', googlePay: 'auto' },
    };
    const paymentElement = elements.create('payment', paymentOptions);
    paymentStore.setPaymentElement(paymentElement);

    if (paymentContainer.value && alive) {
      paymentElement.mount(paymentContainer.value);
    }
  } catch (error) {
    logError('Failed to initialize payment form:', error);
    showToast('danger', t('payment_error_unexpected'));
  }
});

onUnmounted(() => {
  alive = false;
  paymentStore.teardownPaymentForm();
});

function currentReturnUrl(): string {
  return new URL(router.currentRoute.value.fullPath, window.location.origin).href;
}

async function savePaymentMethod() {
  if (!elements) return;

  try {
    const { error } = await elements.submit();
    if (error) {
      if (error.type === 'card_error' || error.type === 'validation_error') {
        showToast('danger', error.message ?? t('payment_error_unexpected'));
      } else {
        showToast('danger', t('payment_error_unexpected'));
      }
      return;
    }

    await paymentStore.confirmSetup(currentReturnUrl());
    emit('done');
  } catch (error) {
    logError('Failed to save payment method:', error);
    const message = error instanceof Error ? error.message : t('payment_error_unexpected');
    showToast('danger', message);
  }
}

function onCancel() {
  emit('cancel');
}
</script>

<template>
  <LayoutGap direction="column" size="medium" class="add-payment-method-form">
    <div class="add-payment-method-form__error" role="alert" aria-live="assertive" aria-atomic="true">
      <Alert
        v-if="paymentStore.error"
        variant="danger"
        :title="t('billing_payment_error')"
        :message="paymentStore.error"
      />
    </div>

    <div ref="paymentContainer" class="add-payment-method-form__stripe"></div>

    <LayoutGap
      direction="row"
      align="space-between"
      vertical-align="center"
      wrap
      size="small"
      class="add-payment-method-form__actions"
    >
      <Button @click="onCancel">{{ t('billing_go_back') }}</Button>
      <Button variant="primary" :disabled="paymentStore.saving" @click="savePaymentMethod">
        {{ t('billing_payment_add') }}
      </Button>
    </LayoutGap>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.add-payment-method-form {
  max-width: none;
  margin: 0;
  padding: 0;

  &__stripe {
    min-height: 144px;
    margin-bottom: 1.5rem;
    min-width: 0;
    width: 100%;
  }

  &__actions {
    width: 100%;
  }

  &__error {
    color: #fa755a;
    margin-bottom: 16px;
    padding: 12px;
    background: #fff5f5;
    border-radius: 4px;
  }
}
</style>
